/**
 * PlaceBuilding use case (contracts.md). AS3-faithful: escalating tower cost (150 + 30*n).
 * Wall type uses a separate escalating cost scale (30 + 1*n).
 */

import {
  isCellInBounds,
  isCellFree,
  isCellBlockedByField,
  wouldBlockPath,
  cellHasMonster,
  spendMana,
  addMana,
  addTower,
  addWall,
  addAmplifier,
  addTrap,
  addShrine,
  effectiveTowerCost,
  effectiveWallCost,
  effectiveAmplifierCost,
  effectiveTrapCost,
  effectiveShrineCost,
} from "../../domain/session.js";
import type { Cell, BuildingType } from "../../domain/types.js";
import { BuildingType as BT } from "../../domain/types.js";
import type { PlaceBuildingError } from "../../domain/types.js";
import type { RenderPort, AudioPort } from "../ports.js";
import { sessionToView } from "../state-view.js";

export interface PlaceBuildingDeps {
  render: RenderPort;
  audio: AudioPort;
  getSession(): import("../../domain/session.js").GameSession | null;
  getPlacementMode?(): "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine";
}

export function createPlaceBuilding(deps: PlaceBuildingDeps) {
  return function placeBuilding(
    cell: Cell,
    buildingType: BuildingType
  ): { ok: true } | PlaceBuildingError {
    const session = deps.getSession();
    if (!session) return { code: "InvalidCell" };
    if (!isCellInBounds(session, cell)) return { code: "InvalidCell" };
    if (!isCellFree(session, cell)) return { code: "InvalidCell" };
    if (cellHasMonster(session, cell)) return { code: "CellOccupiedByMonster" };
    if (isCellBlockedByField(session, cell)) return { code: "CellBlocked" };
    if (buildingType !== BT.Trap && wouldBlockPath(session, cell)) return { code: "PathBlocked" };
    if (buildingType === BT.Wall) {
      const cost = effectiveWallCost(session);
      if (session.mana.current < cost) return { code: "NotEnoughMana" };
      if (!spendMana(session, cost)) return { code: "NotEnoughMana" };
      if (!addWall(session, cell)) { addMana(session, cost); return { code: "PathBlocked" }; }
    } else if (buildingType === BT.Amplifier) {
      const cost = effectiveAmplifierCost(session);
      if (session.mana.current < cost) return { code: "NotEnoughMana" };
      if (!spendMana(session, cost)) return { code: "NotEnoughMana" };
      if (!addAmplifier(session, cell)) { addMana(session, cost); return { code: "PathBlocked" }; }
    } else if (buildingType === BT.Trap) {
      const cost = effectiveTrapCost(session);
      if (session.mana.current < cost) return { code: "NotEnoughMana" };
      if (!spendMana(session, cost)) return { code: "NotEnoughMana" };
      if (!addTrap(session, cell)) { addMana(session, cost); return { code: "PathBlocked" }; }
    } else if (buildingType === BT.Shrine) {
      const cost = effectiveShrineCost(session);
      if (session.mana.current < cost) return { code: "NotEnoughMana" };
      if (!spendMana(session, cost)) return { code: "NotEnoughMana" };
      if (!addShrine(session, cell)) { addMana(session, cost); return { code: "PathBlocked" }; }
    } else {
      const cost = effectiveTowerCost(session);
      if (session.mana.current < cost) return { code: "NotEnoughMana" };
      if (!spendMana(session, cost)) return { code: "NotEnoughMana" };
      if (!addTower(session, cell, buildingType)) { addMana(session, cost); return { code: "PathBlocked" }; }
    }
    const mode = (deps.getPlacementMode?.() ?? "Tower") as "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine";
    deps.render.update(sessionToView(session, "game", undefined, mode));
    deps.audio.play("build");
    return { ok: true };
  };
}
